import React, {useCallback, useLayoutEffect, useRef, useState} from "react";
import {Edges, TransformControls} from "@react-three/drei";
import {useFrame, useThree} from "@react-three/fiber";
import ModuleDetailMesh from "./ModuleDetailMesh";
import DetailCornerHandles from "./DetailCornerHandles";
import {
    applyAxesToObject,
    axesToEulerDegrees,
    cloneAxes,
    IDENTITY_AXES,
} from "./orientationAxes";
import {
    cubeLocalToWorld,
    getDominantOrthoAxis,
    getSnapApproachPreview,
    orthoAxisFromTransformControls,
    projectLocalToOrthoAxis,
    resolveDragPose,
    snapLocalToOtherDetails,
    worldPointToCubeLocal,
} from "./snapPlacement";

const hideMultiAxisTransformHandles = (controls) => {
    if (!controls) return;
    controls.traverse((obj) => {
        const name = obj.name;
        if (name === "XY" || name === "YZ" || name === "XZ" || name === "XYZ") {
            obj.visible = false;
            obj.scale.setScalar(1e-10);
        }
    });
};

/**
 * Зелёный куб в СК модуля: (0,0,0) — угол, из него +X/+Y/+Z внутрь объёма
 * (как DetailPanelAxes и точка привязки на сцене родителя).
 */
const EmbeddedReferenceCube = ({
    sizeM,
    selected = false,
    shellTransparent = false,
    loading = false,
}) => {
    const {width, height, depth} = sizeM;
    const position = [width / 2, height / 2, depth / 2];

    if (loading) {
        return (
            <mesh
                position={position}
                userData={{module3dRole: "embedded-shell", module3dLoading: true}}
            >
                <boxGeometry args={[width, height, depth]}/>
                <meshBasicMaterial
                    color="#78909c"
                    wireframe
                    transparent
                    opacity={0.9}
                    depthWrite={false}
                />
            </mesh>
        );
    }

    return (
        <mesh
            position={position}
            userData={{module3dRole: "embedded-shell"}}
        >
            <boxGeometry args={[width, height, depth]}/>
            <meshStandardMaterial
                color={shellTransparent ? "#ffffff" : (selected ? "#388e3c" : "#2e7d32")}
                transparent
                opacity={shellTransparent ? 0.22 : (selected ? 0.42 : 0.28)}
                depthWrite={!shellTransparent}
            />
            <Edges
                color={shellTransparent ? "#000000" : (selected ? "#f9a825" : "#1b5e20")}
                threshold={15}
            />
        </mesh>
    );
};

const EmbeddedModuleChildMesh = ({
    child,
    showHoles,
    showGrooves,
    showOtherMachining,
    shellTransparent = false,
    pickable = true,
    onSelect,
}) => {
    const groupRef = useRef();

    useLayoutEffect(() => {
        if (!groupRef.current) return;
        // Внутри встраиваемого модуля (0,0,0) — угол куба, без getCubeOrigin.
        // cubeLocalToWorld — только для СК родителя (ReferenceCube по центру XZ).
        const local = child.localPosition || {x: 0, y: 0, z: 0};
        groupRef.current.position.set(local.x, local.y, local.z);
        applyAxesToObject(groupRef.current, child.axes, child.faceFlip);
    }, [child.localPosition, child.axes, child.faceFlip]);

    return (
        <group ref={groupRef}>
            <ModuleDetailMesh
                detailData={child.detailData}
                textureUrl={child.textureUrl}
                textureScaleMm={child.textureScaleMm}
                textureRotationDeg={child.textureRotationDeg}
                selected={false}
                showHoles={showHoles}
                showGrooves={showGrooves}
                showOtherMachining={showOtherMachining}
                shellTransparent={shellTransparent}
                renderOrder={child.sceneDrawOrder ?? 0}
                onSelect={pickable ? onSelect : undefined}
            />
        </group>
    );
};

/** Встраиваемый модуль на сцене родителя: отрисовка, выбор, gizmo и привязка. */
const PlacedEmbeddedModuleGroup = ({
    inst,
    parentModuleSizeM,
    selected = false,
    selectionActive = false,
    addingMode = false,
    snapEnabled = true,
    otherInstances = [],
    onSelect,
    onTransformEnd,
    gizmoDraggingRef,
    interactionLocked = false,
    suppressSelectionVisual = false,
    showHoles = true,
    showGrooves = true,
    showOtherMachining = true,
    shellTransparent = false,
    onSnapApproachChange = null,
    snapApproach = null,
    orthoEnabled = false,
    onSelectedRightPointerDown = null,
}) => {
    const groupRef = useRef();
    const transformControlsRef = useRef(null);
    const draggingRef = useRef(false);
    const dragSourceRef = useRef(null);
    const liveSnapPrevLocalRef = useRef(null);
    const orthoStartLocalRef = useRef(null);
    const orthoLockedAxisRef = useRef(null);
    const activeSnapNodeRef = useRef(null);
    const [object, setObject] = useState(null);
    const {controls: orbitControls} = useThree();

    const setGroupRef = useCallback((node) => {
        groupRef.current = node;
        setObject((prev) => (prev === node ? prev : node));
    }, []);

    useLayoutEffect(() => {
        if (draggingRef.current || !groupRef.current) return;
        const world = cubeLocalToWorld(inst.localPosition, parentModuleSizeM);
        groupRef.current.position.set(world.x, world.y, world.z);
        applyAxesToObject(groupRef.current, inst.axes || IDENTITY_AXES, Boolean(inst.faceFlip));
    }, [inst.localPosition, inst.axes, inst.faceFlip, parentModuleSizeM]);

    useFrame(() => {
        if (!orthoEnabled) return;
        hideMultiAxisTransformHandles(transformControlsRef.current);
    });

    const loadingOntoScene = !inst.childrenHydrated;
    const showSelectionChrome = selected && !suppressSelectionVisual && !loadingOntoScene;
    const pickable = Boolean(addingMode || selected || !selectionActive) && !interactionLocked;

    const resolveOrthoAxis = useCallback((rawLocal) => {
        const start = orthoStartLocalRef.current;
        if (!start) return null;
        if (dragSourceRef.current === "gizmo") {
            const fromGizmo = orthoAxisFromTransformControls(transformControlsRef.current?.axis);
            if (fromGizmo) {
                orthoLockedAxisRef.current = fromGizmo;
                return fromGizmo;
            }
            if (orthoLockedAxisRef.current) {
                return orthoLockedAxisRef.current;
            }
        }
        return getDominantOrthoAxis(start, rawLocal);
    }, []);

    const applyOrthoToLocal = useCallback((rawLocal) => {
        const start = orthoStartLocalRef.current;
        if (!start) return rawLocal;
        return projectLocalToOrthoAxis(start, rawLocal, resolveOrthoAxis(rawLocal));
    }, [resolveOrthoAxis]);

    const commitTransform = useCallback(() => {
        const obj = groupRef.current;
        if (!obj) return;
        const axes = cloneAxes(inst.axes || IDENTITY_AXES);
        const faceFlip = Boolean(inst.faceFlip);
        let localPosition = worldPointToCubeLocal(obj.position, parentModuleSizeM);
        if (orthoEnabled && orthoStartLocalRef.current) {
            localPosition = applyOrthoToLocal(localPosition);
        }
        const raw = {
            localPosition,
            axes,
            faceFlip,
            euler: axesToEulerDegrees(axes),
        };
        const snapped = resolveDragPose({
            ...raw,
            inst,
            others: otherInstances,
            snapEnabled,
            cubeSize: parentModuleSizeM,
            activeNodeLocal: activeSnapNodeRef.current,
        });
        if (orthoEnabled && orthoStartLocalRef.current) {
            const axis = resolveOrthoAxis(localPosition);
            snapped.localPosition = projectLocalToOrthoAxis(
                orthoStartLocalRef.current,
                snapped.localPosition,
                axis,
            );
        }
        const world = cubeLocalToWorld(snapped.localPosition, parentModuleSizeM);
        obj.position.set(world.x, world.y, world.z);
        applyAxesToObject(obj, snapped.axes, faceFlip);
        onTransformEnd?.(inst.instanceId, {
            localPosition: snapped.localPosition,
            axes: snapped.axes,
            faceFlip,
            euler: snapped.euler || raw.euler,
        });
    }, [
        applyOrthoToLocal,
        inst,
        onTransformEnd,
        otherInstances,
        orthoEnabled,
        parentModuleSizeM,
        resolveOrthoAxis,
        snapEnabled,
    ]);

    const applyLiveSnap = useCallback(() => {
        if (!groupRef.current) return;
        const obj = groupRef.current;
        let local = worldPointToCubeLocal(obj.position, parentModuleSizeM);
        if (orthoEnabled && orthoStartLocalRef.current) {
            const constrained = applyOrthoToLocal(local);
            if (
                constrained.x !== local.x
                || constrained.y !== local.y
                || constrained.z !== local.z
            ) {
                const world = cubeLocalToWorld(constrained, parentModuleSizeM);
                obj.position.set(world.x, world.y, world.z);
                local = constrained;
            }
        }
        if (snapEnabled) {
            const preview = getSnapApproachPreview({
                inst,
                localPosition: local,
                axes: cloneAxes(inst.axes || IDENTITY_AXES),
                others: otherInstances,
                cubeSize: parentModuleSizeM,
                activeNodeLocal: activeSnapNodeRef.current,
            });
            onSnapApproachChange?.((prev) => {
                if (!preview && !prev) return prev;
                return preview;
            });
        }
        if (!snapEnabled) return;
        let snapped = snapLocalToOtherDetails({
            inst,
            localPosition: local,
            axes: cloneAxes(inst.axes || IDENTITY_AXES),
            others: otherInstances,
            cubeSize: parentModuleSizeM,
            mode: "live",
            previousLocalPosition: liveSnapPrevLocalRef.current,
            activeNodeLocal: activeSnapNodeRef.current,
        });
        if (orthoEnabled && orthoStartLocalRef.current) {
            const axis = resolveOrthoAxis(local);
            snapped = projectLocalToOrthoAxis(orthoStartLocalRef.current, snapped, axis);
        }
        liveSnapPrevLocalRef.current = {...local};
        if (
            snapped.x === local.x
            && snapped.y === local.y
            && snapped.z === local.z
        ) {
            return;
        }
        const world = cubeLocalToWorld(snapped, parentModuleSizeM);
        obj.position.set(world.x, world.y, world.z);
        liveSnapPrevLocalRef.current = {...snapped};
    }, [
        applyOrthoToLocal,
        inst,
        orthoEnabled,
        otherInstances,
        parentModuleSizeM,
        resolveOrthoAxis,
        snapEnabled,
        onSnapApproachChange,
    ]);

    const beginDrag = useCallback((source) => {
        const startLocal = groupRef.current
            ? worldPointToCubeLocal(groupRef.current.position, parentModuleSizeM)
            : null;
        draggingRef.current = true;
        dragSourceRef.current = source;
        liveSnapPrevLocalRef.current = startLocal;
        orthoStartLocalRef.current = startLocal ? {...startLocal} : null;
        orthoLockedAxisRef.current = source === "gizmo"
            ? orthoAxisFromTransformControls(transformControlsRef.current?.axis)
            : null;
        if (gizmoDraggingRef) gizmoDraggingRef.current = true;
        if (orbitControls) orbitControls.enabled = false;
    }, [gizmoDraggingRef, orbitControls, parentModuleSizeM]);

    const endDrag = useCallback(() => {
        draggingRef.current = false;
        liveSnapPrevLocalRef.current = null;
        onSnapApproachChange?.(null);
        if (orbitControls) orbitControls.enabled = true;
        commitTransform();
        activeSnapNodeRef.current = null;
        orthoStartLocalRef.current = null;
        orthoLockedAxisRef.current = null;
        dragSourceRef.current = null;
        window.setTimeout(() => {
            if (gizmoDraggingRef) gizmoDraggingRef.current = false;
        }, 0);
    }, [commitTransform, gizmoDraggingRef, onSnapApproachChange, orbitControls]);

    const handleSelect = useCallback((event) => {
        event.stopPropagation();
        if (addingMode) return;
        if (gizmoDraggingRef?.current) return;
        onSelect?.(inst.instanceId);
    }, [addingMode, gizmoDraggingRef, inst.instanceId, onSelect]);

    useLayoutEffect(() => {
        const root = groupRef.current;
        if (!root) return undefined;
        const touched = [];
        root.traverse((obj) => {
            if (obj.userData?.module3dRole === "helper" || obj.parent?.userData?.module3dRole === "helper") {
                obj.raycast = () => null;
                return;
            }
            if (obj.userData?.module3dRole === "corner-handle") return;
            if (!obj.isMesh && !obj.isLine && !obj.isLineSegments) return;
            if (!pickable) {
                if (!obj.userData.__module3dRaycastSaved) {
                    obj.userData.__module3dRaycastSaved = obj.raycast;
                    obj.raycast = () => null;
                    touched.push(obj);
                }
            } else if (obj.userData.__module3dRaycastSaved) {
                obj.raycast = obj.userData.__module3dRaycastSaved;
                delete obj.userData.__module3dRaycastSaved;
            }
        });
        return () => {
            touched.forEach((obj) => {
                if (obj.userData.__module3dRaycastSaved) {
                    obj.raycast = obj.userData.__module3dRaycastSaved;
                    delete obj.userData.__module3dRaycastSaved;
                }
            });
        };
    }, [pickable, object, showHoles, showGrooves, showOtherMachining, inst.children]);

    return (
        <>
            <group
                ref={setGroupRef}
                userData={{
                    module3dRole: "embedded-module",
                    instanceId: inst.instanceId,
                    exportName: inst.label,
                }}
                onPointerDown={pickable ? (event) => {
                    event.stopPropagation();
                    if (event.button === 2 && selected && !addingMode) {
                        onSelectedRightPointerDown?.(inst.instanceId);
                    }
                } : undefined}
                onClick={pickable ? handleSelect : undefined}
            >
                <EmbeddedReferenceCube
                    sizeM={inst.moduleSizeM}
                    selected={showSelectionChrome}
                    shellTransparent={shellTransparent}
                    loading={loadingOntoScene}
                />
                {(inst.children || []).map((child) => (
                    <EmbeddedModuleChildMesh
                        key={child.instanceId}
                        child={child}
                        showHoles={showHoles}
                        showGrooves={showGrooves}
                        showOtherMachining={showOtherMachining}
                        shellTransparent={shellTransparent}
                        pickable={pickable}
                        onSelect={handleSelect}
                    />
                ))}
                {showSelectionChrome && !addingMode && !interactionLocked && (
                    <DetailCornerHandles
                        inst={inst}
                        enabled
                        renderOnTop
                        groupRef={groupRef}
                        orthoEnabled={orthoEnabled}
                        onDragStart={(localPosition) => {
                            activeSnapNodeRef.current = localPosition;
                            beginDrag("corner");
                        }}
                        onDrag={applyLiveSnap}
                        onDragEnd={(localPosition) => {
                            activeSnapNodeRef.current = localPosition;
                            endDrag();
                        }}
                        shrinkScale={
                            snapApproach?.sourceInstanceId === inst.instanceId
                                ? snapApproach.scale
                                : 1
                        }
                        shrinkLocal={
                            snapApproach?.sourceInstanceId === inst.instanceId
                                ? snapApproach.sourceLocal
                                : null
                        }
                    />
                )}
            </group>
            {showSelectionChrome && object && !addingMode && !interactionLocked && (
                <TransformControls
                    ref={transformControlsRef}
                    object={object}
                    mode="translate"
                    space="world"
                    size={0.85}
                    onMouseDown={(event) => {
                        event?.stopPropagation?.();
                        activeSnapNodeRef.current = [0, 0, 0];
                        beginDrag("gizmo");
                    }}
                    onObjectChange={applyLiveSnap}
                    onMouseUp={(event) => {
                        event?.stopPropagation?.();
                        endDrag();
                    }}
                />
            )}
        </>
    );
};

export default PlacedEmbeddedModuleGroup;
